// Socket Service for Meducate real-time notifications
// Handles WebSocket connections, message handling, and reconnection strategy

const debug = true;

class SocketService {
  static instance;

  constructor() {
    this.socket = null;
    this.adminSocket = null;
    this.isConnected = false;
    this.isAdmin = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 2000;
    this.handlers = {
      onMessage: () => {},
      onConnect: () => {},
      onDisconnect: () => {},
      onTokenExpiry: () => {},
      onUnreadCount: () => {},
      onNotificationsList: () => {},
      onNewNotification: () => {},
      onReadStatusUpdate: () => {},
      onAllReadStatusUpdate: () => {},
      onError: () => {},
    };
    this.adminHandlers = {
      onConnect: () => {},
      onDisconnect: () => {},
      onMessage: () => {},
      onSystemStats: () => {}, // Add handler for system stats
    };
    this.tokenExpiryTime = 0;
    this.tokenExpiryTimer = null;

    // Add event listeners support
    this.eventListeners = {};
  }

  static getInstance() {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  // Set message handlers
  setHandlers(handlers) {
    this.handlers = { ...this.handlers, ...handlers };
  }

  // Set admin message handlers
  setAdminHandlers(handlers) {
    this.adminHandlers = { ...this.adminHandlers, ...handlers };
  }

  // Connect to the WebSocket server
  // SEC-019: the access token is an httpOnly cookie now — the browser
  // attaches it to the WS handshake automatically (same-origin, same as any
  // other request), so there's no token to accept or put in the URL
  // anymore.
  // `isAdminCandidate` comes from the caller's own knowledge of the user's
  // role (Redux user object) — we only attempt the admin socket when it's
  // plausible, so non-admin users (the vast majority) don't trigger a
  // doomed connection attempt and the browser's own unsuppressable
  // "WebSocket connection failed" console log on every page load.
  connect(isAdminCandidate = false) {
    if (this.socket) {
      this.disconnect();
    }
    this.isAdminCandidate = isAdminCandidate;

    try {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

      // Fix URL construction - use current location host for production, localhost:8000 for dev
      const apiUrl =
        import.meta.env.VITE_APP_API_URL || "http://localhost:8000";
      const isLocalhost = apiUrl.includes("localhost");
      const host = isLocalhost ? "localhost:8000" : window.location.host;

      const wsUrl = `${protocol}//${host}/ws/notifications/`;

      this.socket = new WebSocket(wsUrl);

      // Connection opened
      this.socket.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 2000;
        this.handlers.onConnect();

        // Only attempt the admin socket for users who plausibly are one —
        // the backend still has the final say (closes with 4003 if not),
        // this just avoids trying at all for ordinary users.
        if (this.isAdminCandidate) {
          this.connectToAdminSocket();
        }
      };

      // Connection closed
      this.socket.onclose = (event) => {
        this.isConnected = false;
        this.clearTokenExpiryTimer();

        // Handle different close codes
        if (event.code === 4001) {
          // Token expired
          this.handlers.onTokenExpiry(-1);
        } else if (event.code === 4002) {
          // Invalid token
          this.handlers.onError("Invalid authentication token");
        }

        this.handlers.onDisconnect(event);

        // Attempt reconnection for unexpected closures
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002) {
          this.attemptReconnect();
        }
      };

      // Connection error
      this.socket.onerror = (error) => {
        debug && console.error("[SocketService] WebSocket error:", error);
        debug &&
          console.error("[SocketService] Error details:", {
            url: wsUrl,
            readyState: this.socket?.readyState,
            error: error,
          });
        this.handlers.onError("Connection error");
      };

      // Receive message
      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          debug &&
            console.error(
              "[SocketService] Error parsing WebSocket message:",
              error,
            );
        }
      };

      return true;
    } catch (error) {
      debug &&
        console.error("[SocketService] Error creating WebSocket:", error);
      this.handlers.onError(`Failed to connect: ${error.message}`);
      return false;
    }
  }

  // Connect to admin WebSocket
  // SEC-019: there's no raw token to decode client-side anymore to check
  // admin claims ahead of time (that was never a real security boundary
  // anyway) — just attempt the connection and let the backend decide; it
  // already closes with code 4003 for non-admins, handled in onclose below.
  connectToAdminSocket() {
    if (this.adminSocket) {
      this.adminSocket.close();
    }

    try {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

      // Fix URL construction - use current location host for production, localhost:8000 for dev
      const apiUrl =
        import.meta.env.VITE_APP_API_URL || "http://localhost:8000";
      const isLocalhost = apiUrl.includes("localhost");
      const host = isLocalhost ? "localhost:8000" : window.location.host;

      const adminWsUrl = `${protocol}//${host}/ws/admin-notifications/`;

      // Create admin WebSocket connection
      this.adminSocket = new WebSocket(adminWsUrl);

      // Admin connection opened (confirms admin status)
      this.adminSocket.onopen = () => {
        this.isAdmin = true;
        this.adminHandlers.onConnect();
      };

      // Admin connection closed
      this.adminSocket.onclose = (event) => {
        if (event.code === 4001) {
          // Token expired, already handled by main socket
        } else if (event.code === 4003) {
          // Not an admin, no need to log or notify
          this.isAdmin = false;
        }

        this.adminHandlers.onDisconnect(event);
      };

      // Admin connection error
      this.adminSocket.onerror = (error) => {
        debug && console.error("[SocketService] Admin WebSocket error:", error);
      };

      // Receive admin message
      this.adminSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle specific admin message types
          if (
            data.type === "system_stats_update" ||
            data.type === "system_stats"
          ) {
            // Check if we have stats data
            if (!data.stats) {
              console.error(
                "❌ [SocketService] No stats data in system stats message",
                data,
              );
            }

            this.adminHandlers.onSystemStats(data);
            // Emit the system stats event for components to listen
            this.emit("system_stats", data.stats);

            // Also emit with the exact event name for components listening specifically for it
            this.emit(data.type, data.stats);
          } else {
            // General message handling
            this.adminHandlers.onMessage(data);
            // Also emit by type so independent listeners (e.g. the HLS
            // conversion queue panel) don't have to share/override
            // adminHandlers.onMessage with NotificationContext's own handler
            this.emit(data.type, data);
          }
        } catch (error) {
          debug &&
            console.error(
              "[SocketService] Error parsing admin WebSocket message:",
              error,
            );
        }
      };
    } catch (error) {
      debug &&
        console.error("[SocketService] Error creating Admin WebSocket:", error);
    }
  }

  // Handle incoming messages
  handleMessage(data) {
    switch (data.type) {
      case "token_expiration":
        this.handleTokenExpiry(data.expires_in);
        this.handlers.onTokenExpiry(data.expires_in);
        break;

      case "unread_count":
        this.handlers.onUnreadCount(data.count);
        break;

      case "notifications_list":
        this.handlers.onNotificationsList(data.notifications);
        break;

      case "new_notification":
        this.handlers.onNewNotification(data.notification, data.unread_count);

        // Emit event for contribution deletion if applicable
        if (data.notification?.notification_type === "COMMENT_DELETED") {
          this.emit("contributionDeleted", data.notification.data?.comment_id);
        }

        // Emit event for contribution validation if applicable
        if (data.notification?.notification_type === "COMMENT_VALIDATED") {
          this.emit("contributionValidated", {
            comment_id: data.notification.data?.comment_id,
            user_id: data.notification.data?.user_id,
          });
        }

        // Emit event for report rejection if applicable
        if (data.notification?.notification_type === "REPORT_REJECTED") {
          this.emit("reportRejected", data.notification.data?.report_id);
        }

        // Emit a generic notification event
        this.emit("notification", data.notification);
        break;

      case "read_status_update":
        this.handlers.onReadStatusUpdate(
          data.notification_id,
          data.unread_count,
        );
        break;

      case "all_read_status_update":
        this.handlers.onAllReadStatusUpdate(data.unread_count);
        break;

      case "error":
        debug &&
          console.error(`[SocketService] Error from server: ${data.message}`);
        this.handlers.onError(data.message);
        this.emit("error", data.message);
        break;

      default:
        // Still emit the message in case custom handlers are listening
        this.emit(data.type, data);
    }
  }

  // Handle token expiry updates
  handleTokenExpiry(expiresIn) {
    // Clear any existing timer
    this.clearTokenExpiryTimer();

    if (expiresIn <= 0) {
      // Token is already expired
      this.tokenExpiryTime = 0;
      return;
    }

    // Set the new expiry time
    this.tokenExpiryTime = expiresIn;

    // Set up a timer to track token expiry
    this.tokenExpiryTimer = setInterval(() => {
      this.tokenExpiryTime--;

      if (this.tokenExpiryTime <= 0) {
        this.clearTokenExpiryTimer();
      } else if (this.tokenExpiryTime === 30) {
        // Notify when token is about to expire in 30 seconds
        this.handlers.onTokenExpiry(30);
      }
    }, 1000);
  }

  // Clear token expiry timer
  clearTokenExpiryTimer() {
    if (this.tokenExpiryTimer) {
      clearInterval(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
  }

  // Attempt to reconnect
  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        // Check if we're still supposed to be connected before attempting reconnect
        if (!this.isConnected) {
          this.connect(this.isAdminCandidate);
        }
      }, this.reconnectDelay);

      // Exponential backoff with jitter to prevent thundering herd
      const jitter = Math.random() * 1000; // Add up to 1 second of jitter
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5 + jitter, 30000); // Max 30 seconds
      this.reconnectAttempts++;
    } else {
      debug &&
        console.error(
          "[SocketService] WebSocket reconnection failed after maximum attempts",
        );
      this.handlers.onError("Reconnection failed after maximum attempts");
      // Reset reconnection state after final failure
      this.resetReconnectionState();
    }
  }

  // Reset reconnection state
  resetReconnectionState() {
    this.reconnectAttempts = 0;
    this.reconnectDelay = 2000; // Reset to initial delay
  }

  // Disconnect WebSockets
  disconnect() {
    this.clearTokenExpiryTimer();

    if (this.socket) {
      this.socket.close(1000, "Normal closure"); // Normal closure
      this.socket = null;
      this.isConnected = false;
    }

    if (this.adminSocket) {
      this.adminSocket.close(1000, "Normal closure");
      this.adminSocket = null;
      this.isAdmin = false;
    }
  }

  // Get notifications
  getNotifications() {
    return this.send({
      command: "get_notifications",
    });
  }

  // Mark notification as read
  markAsRead(notificationId) {
    return this.send({
      command: "mark_as_read",
      id: notificationId,
    });
  }

  // Mark all notifications as read
  markAllAsRead() {
    return this.send({
      command: "mark_all_read",
    });
  }

  // Send message through WebSocket
  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return true;
    } else {
      debug &&
        console.error(
          "[SocketService] WebSocket not connected, cannot send message",
        );
      return false;
    }
  }

  // Send admin command
  sendAdminCommand(data) {
    if (
      this.isAdmin &&
      this.adminSocket &&
      this.adminSocket.readyState === WebSocket.OPEN
    ) {
      this.adminSocket.send(JSON.stringify(data));
      return true;
    } else {
      debug &&
        console.error(
          "[SocketService] Admin WebSocket not connected or user is not an admin",
        );
      return false;
    }
  }

  // Check if WebSocket is connected
  isSocketConnected() {
    return this.isConnected;
  }

  // Check if user is admin
  isUserAdmin() {
    return this.isAdmin;
  }

  // Get remaining token expiry time in seconds
  getTokenExpiryTime() {
    return this.tokenExpiryTime;
  }

  // Event emitter pattern implementation
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
    return this; // For chaining
  }

  off(event, callback) {
    if (!this.eventListeners[event]) return this;

    if (callback) {
      // Remove specific callback
      this.eventListeners[event] = this.eventListeners[event].filter(
        (cb) => cb !== callback,
      );
    } else {
      // Remove all callbacks for this event
      delete this.eventListeners[event];
    }
    return this; // For chaining
  }

  emit(event, ...args) {
    if (!this.eventListeners[event]) return false;

    this.eventListeners[event].forEach((callback) => {
      try {
        callback(...args);
      } catch (error) {
        debug &&
          console.error(
            `[SocketService] Error in event listener for ${event}:`,
            error,
          );
      }
    });
    return true;
  }
}

export const socketService = SocketService.getInstance();
